"use client";

import { ArrowRight, Play } from "lucide-react";
import { motion } from "framer-motion";
import Image from "next/image";

/* Orchestrates the card reveal: each cluster staggers its children, and the
   `custom` prop lets the right-hand cluster start a beat after the left one. */
const cluster = {
  hidden: {},
  show: (i = 0) => ({
    transition: { staggerChildren: 0.14, delayChildren: 0.15 + i * 0.22 },
  }),
};

/* Each card rises from below + fades in. Kept transform-free at rest (y: 0) so
   the inner card div keeps owning its rotate/translate styling. */
const rise = {
  hidden: { opacity: 0, y: 70 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};

const HeroSection = () => {
  return (
    <section
      style={{
        backgroundColor: "#F5F5F7",
        // 1. Swapped hex colors for semi-transparent RGBA so the grid shows through
        backgroundImage:
          "linear-gradient(to bottom, rgba(232, 236, 247, 0.7), rgba(255, 255, 255, 0.3)), url(/assets/hero/grid.svg)",
        // 2. Cleaned up background properties to perfectly align a single gradient + single image
        backgroundPosition: "top center",
        backgroundSize: "cover",
      }}
      // 3. Added Tailwind backdrop-blur class to handle the blur effect beautifully
      className="relative lg:mt-2 pt-14 sm:pt-16 lg:pt-20   lg:w-[calc(100%-2rem)] 2xl:w-[calc(100%-4rem)] shadow-none lg:rounded-3xl mx-auto px-5 sm:px-8 overflow-hidden  min-h-[calc(100vh-20px)] md:min-h-[calc(100vh-160px)] lg:h-[calc(100vh-80px)] flex items-center flex-col backdrop-blur-[6px]"
    >
      {/* ── Headline block ── */}
      <header className="relative z-10 mx-auto flex max-w-[700px] 2xl:max-w-[800px] 3xl:max-w-[900px] 4xl:max-w-[1100px]  flex-col items-center text-center justify-center">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-2 text-xs font-medium text-[#53555C] shadow-sm 3xl:text-sm">
          <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[#7F7FFF]/20">
            <span className="block h-1.5 w-1.5 rounded-full bg-[#7F7FFF]" />
          </span>
          <span className="leading-none whitespace-nowrap font-kalam">
            AI Powered Ads Creations
          </span>
        </div>
        {/* 
        <h1 className="text-[28px] sm:text-[38px] md:text-[40px] leading-tight lg:text-[46px] 3xl:text-[64px] 4xl:text-[80px] font-medium tracking-[-0.5px]  text-[#0E0B1A]"> */}
        <h1 className="solution-heading">
          From a single brief to fifty ads your audience can&apos;t scroll past.
        </h1>

        <p className="body-secondary-text mt-5 lg:max-w-[560px] 2xl:max-w-[700px]">
          Copy, image, video, bulk variants and smart resizing every ad in your
          brand voice, generated from one prompt and pushed to every channel in
          minutes.
        </p>

        <div className="mt-8 flex w-full flex-col items-center justify-center gap-3 sm:w-auto sm:flex-row">
          <a
            href="https://app.imagive.ai?redirect=/users/image-generator"
            target="_blank"
            className="btn-primary group inline-flex w-full items-center justify-center gap-2 font-medium sm:w-auto"
          >
            Create your first 10 ads
            <ArrowRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-1"
            />
          </a>
          {/* <a
            href="https://app.imagive.ai/"
            target="_blank"
            className="btn-secondary group inline-flex w-full items-center justify-center gap-2 font-medium sm:w-auto"
          >
            <Play
              size={16}
              className="transition-transform duration-300 group-hover:scale-110"
            />
            Watch 90s tour
          </a> */}
        </div>
      </header>

      {/* ── Desktop card clusters (unchanged layout, now reveal on load) ── */}
      <div className="pointer-events-none absolute  inset-x-0 bottom-0 ">
        {/* ── LEFT cluster ── */}
        <motion.div
          className="absolute -bottom-5 left-0 flex translate-y-[14%] items-end"
          variants={cluster}
          custom={0}
          initial="hidden"
          animate="show"
        >
          {/* Same-day-delivery card → Image Generate */}
          <motion.div variants={rise}>
            <div className="relative z-1 -translate-x-[60px] translate-y-4 rotate-[8deg]">
              <span className="absolute rotate-[-8deg] -top-10 left-14 md:-top-12  md:left-20 z-10 flex items-start">
                <Image
                  src="/assets/adCreation/image-generator-arrow.svg"
                  alt="image generator arrow"
                  width={58}
                  height={47}
                  className=" h-auto w-8 md:w-12"
                />
                <span className="whitespace-nowrap font-kalam text-[10px] md:text-sm font-bold text-[#130FEC]">
                  Image Generate
                </span>
              </span>
              <Image
                src="/assets/adCreation/container2.svg"
                alt="Same day delivery ad creative"
                width={240}
                height={266}
                className="h-auto w-[160px] md:w-[180px] lg:w-[200px] xl:w-[240px] 2xl:w-[300px] 3xl:w-[400px] drop-shadow-2xl"
              />
            </div>
          </motion.div>

          {/* Video card → Video generate */}
          <motion.div variants={rise} className="-ml-12 mb-1">
            <div className="relative -translate-x-[50px]  translate-y-4 rotate-[25deg]">
              <span className="absolute   md:left-44 -top-16 md:-top-0 rotate-[-65deg] md:rotate-[-25deg] z-20 flex items-center gap-1">
                <Image
                  src="/assets/adCreation/video-generator-arrow.svg"
                  alt=""
                  width={49}
                  height={49}
                  className="h-auto w-7 md:w-10"
                />
                <span className="whitespace-nowrap font-kalam  text-[10px] md:text-sm font-bold text-[#130FEC]">
                  Video generate
                </span>
              </span>
              <Image
                src="/assets/adCreation/container1.svg"
                alt="Video ad creative"
                width={165}
                height={211}
                className="h-auto w-[120px] md:w-[150px] lg:w-[140px] xl:w-[165px] 2xl:w-[200px]  3xl:w-[280px] drop-shadow-2xl"
              />
            </div>
          </motion.div>
        </motion.div>

        {/* ── RIGHT cluster ── */}
        <motion.div
          className="absolute bottom-0 right-0 flex translate-y-[14%] items-end"
          variants={cluster}
          custom={1}
          initial="hidden"
          animate="show"
        >
          {/* Multiple-sizes card → Smart Resizing */}
          <motion.div variants={rise} className="relative z-1">
            <div className="translate-x-56 rotate-[-4deg]">
              <span className="absolute -top-10 md:-top-12 lg:-top-10 xl:-top-14 -left-6 md:-left-24 z-20 flex flex-col items-end">
                <span className="whitespace-nowrap font-kalam text-[10px]  md:text-sm font-bold text-[#130FEC]">
                  Smart Resizing
                </span>
                <Image
                  src="/assets/adCreation/smart-resizer-arrow.svg"
                  alt=""
                  width={41}
                  height={40}
                  className="h-auto w-6 md:w-9 rotate-45 md:rotate-0"
                />
              </span>
              <Image
                src="/assets/adCreation/container3.svg"
                alt="Multiple sizes ad creative"
                width={360}
                height={175}
                className="h-auto w-[270px] md:w-[300px] lg:w-[300px] xl:w-[360px] 2xl:w-[400px] 3xl:w-[500px] rounded-xl drop-shadow-2xl"
              />
            </div>
          </motion.div>

          {/* Wellbeing-nutrition card → Bulk ad create */}
          <motion.div variants={rise}>
            <div className="relative translate-x-12 rotate-[-12deg]">
              <span className="absolute -top-14 right-10 z-20 flex flex-col items-end">
                <span className="whitespace-nowrap font-kalam text-[10px] md:text-sm font-bold text-[#130FEC]">
                  Bulk ad create
                </span>
                <Image
                  src="/assets/adCreation/bulk-ad-creative-arrow.svg"
                  alt=""
                  width={54}
                  style={{
                    borderRadius: "10px",
                  }}
                  height={42}
                  className="mr-2 mt-1 h-auto w-8 md:w-12"
                />
              </span>
              <Image
                src="/assets/adCreation/container4.svg"
                alt="bulk ad creative"
                width={210}
                height={260}
                className="h-auto w-[120px] md:w-[150px] lg:w-[130px] xl:w-[210px] 2xl:w-[280px] 3xl:w-[350px]  drop-shadow-2xl"
              />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
